import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/Modal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faCalculator } from '@fortawesome/free-solid-svg-icons';
import NotificationSystem from '../../components/common/NotificationSystem';
import DataService from '../../services/DataService';
import AIHandler from '../../utils/AIHandler';

const AccountModal = ({ isActive, onClose, onSubmit, account = null }) => {
    const isDemo = process.env.REACT_APP_IS_DEMO === 'true';
    const defaultAccountData = useMemo(() => ({
        name: '',
        typeId: null,
        taxId: 'FR', // Ajout du champ taxId dans l'état par défaut
        addresses: [{
            address: '',
            postalCode: '',
            city: '',
            isPrimary: true,
            isBillingLocation: true,
            distanceKm: 0
        }],
        contacts: [{
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            isPrimary: true,
            isBillingContact: true
        }]
    }), []);

    const [accountData, setAccountData] = useState(defaultAccountData);
    const [accountTypes, setAccountTypes] = useState([]);

    // Charger les types de compte
    useEffect(() => {
        async function fetchAccountTypes() {
            try {
                const types = await DataService.getTypes('AccountType');
                setAccountTypes(types);
            } catch (error) {
                console.error('Erreur lors du chargement des types de compte:', error);
            }
        }
        fetchAccountTypes();
    }, []);

    // Modifier l'effet pour s'assurer que accountData est toujours un objet valide
    useEffect(() => {
        if (!isActive) {
            setAccountData(defaultAccountData);
        } else if (account) {
            // S'assurer que les tableaux addresses et contacts sont correctement formatés
            setAccountData({
                ...account,
                addresses: Array.isArray(account.addresses) && account.addresses.length > 0 
                    ? account.addresses.map(addr => ({
                        ...addr,
                        isPrimary: !!addr.isPrimary,
                        isBillingLocation: !!addr.isBillingLocation,
                        distanceKm: addr.distanceKm || 0
                    }))
                    : [defaultAccountData.addresses[0]],
                contacts: Array.isArray(account.contacts) && account.contacts.length > 0
                    ? account.contacts.map(contact => ({
                        ...contact,
                        isPrimary: !!contact.isPrimary,
                        isBillingContact: !!contact.isBillingContact
                    }))
                    : [defaultAccountData.contacts[0]]
            });
        } else {
            const defaultType = accountTypes.find(t => t.order === 0);
            setAccountData({
                ...defaultAccountData,
                typeId: defaultType ? defaultType.id : null
            });
        }
    }, [isActive, account, defaultAccountData, accountTypes]);

    const [notification, setNotification] = useState({ message: '', type: '', show: false });

    const validateEmail = (email) => {
        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Validation des emails
        let invalidEmails = [];
        
        accountData.contacts.forEach((contact, index) => {
            if (!validateEmail(contact.email)) {
                invalidEmails.push(`${contact.firstName} ${contact.lastName}`);
            }
        });

        if (invalidEmails.length > 0) {
            setNotification({
                message: `Email(s) invalide(s) pour : ${invalidEmails.join(', ')}`,
                type: 'error',
                show: true
            });
            return;
        }

        // Soumettre directement les données du compte
        onSubmit(accountData);
        onClose(); // Fermer le modal après soumission
    };

    const addAddress = () => {
        setAccountData(prev => ({
            ...prev,
            addresses: [...prev.addresses, {
                address: '',
                postalCode: '',
                city: '',
                isPrimary: false,
                isBillingLocation: false,
                distanceKm: 0
            }]
        }));
    };

    const removeAddress = (index) => {
        setAccountData(prev => ({
            ...prev,
            addresses: prev.addresses.filter((_, i) => i !== index)
        }));
    };

    const updateAddress = (index, value, field = 'address') => {
        setAccountData(prev => ({
            ...prev,
            addresses: prev.addresses.map((addr, i) => {
                if (i === index) {
                    if ((field === 'isPrimary' || field === 'isBillingLocation') && value) {
                        return { ...addr, [field]: value };
                    }
                    return { ...addr, [field]: value };
                }
                if (field === 'isPrimary') {
                    return { ...addr, isPrimary: false };
                }
                if (field === 'isBillingLocation') {
                    return { ...addr, isBillingLocation: false };
                }
                return addr;
            })
        }));
    };

    const addContact = () => {
        setAccountData(prev => ({
            ...prev,
            contacts: [...prev.contacts, {
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                isPrimary: false,
                isBillingContact: false
            }]
        }));
    };

    const removeContact = (index) => {
        setAccountData(prev => ({
            ...prev,
            contacts: prev.contacts.filter((_, i) => i !== index)
        }));
    };

    const updateContact = (index, value, field) => {
        setAccountData(prev => ({
            ...prev,
            contacts: prev.contacts.map((contact, i) => {
                if (i === index) {
                    if (field === 'email' && !validateEmail(value)) {
                        setNotification({
                            message: 'Format d\'email invalide',
                            type: 'warning',
                            show: true
                        });
                    }
                    if ((field === 'isPrimary' || field === 'isBillingContact') && value) {
                        return { ...contact, [field]: value };
                    }
                    return { ...contact, [field]: value };
                }
                if (field === 'isPrimary') {
                    return { ...contact, isPrimary: false };
                }
                if (field === 'isBillingContact') {
                    return { ...contact, isBillingContact: false };
                }
                return contact;
            })
        }));
    };

    const isGeneralInfoValid = useMemo(() => {
        return accountData.name.trim() !== '' && accountData.typeId !== null;
    }, [accountData.name, accountData.typeId]);

    // eslint-disable-next-line
    const isPrimaryAddressValid = useMemo(() => {
        const primaryAddress = accountData.addresses.find(addr => addr.isPrimary);
        return primaryAddress && 
               primaryAddress.address.trim() !== '';
        // Suppression des validations trop strictes sur code postal et ville
    }, [accountData.addresses]);

    // eslint-disable-next-line
    const isPrimaryContactValid = useMemo(() => {
        const primaryContact = accountData.contacts.find(contact => contact.isPrimary);
        return primaryContact &&
               primaryContact.firstName.trim() !== '' &&
               primaryContact.lastName.trim() !== '';
        // Suppression des validations trop strictes sur email et téléphone
    }, [accountData.contacts]);

    const calculateDistance = async (address) => {
        try {
            // Récupérer le type de compte principal
            const accountTypes = await DataService.getTypes('AccountType');
            const principalType = accountTypes.find(type => type.data === 'Principal');
            
            if (!principalType) {
                console.error('Type de compte principal non trouvé');
                return;
            }

            // Récupérer le compte principal
            const accounts = await DataService.filter('accounts', { typeId: principalType.id });
            const principalAccount = accounts[0];
            
            if (!principalAccount) {
                console.error('Compte principal non trouvé');
                return;
            }

            // Récupérer l'adresse principale du compte principal
            const principalAccountDetails = await DataService.getAccountWithDetails(principalAccount.id);
            const principalAddress = principalAccountDetails.addresses.find(addr => addr.isPrimary);

            if (!principalAddress) {
                console.error('Adresse principale non trouvée');
                return;
            }

            // Récupérer le prompt DISTANCEKM
            const distancePrompt = await DataService.getByField('aiPrompts', 'name', 'DISTANCEKM');
            
            if (!distancePrompt) {
                console.error('Prompt DISTANCEKM non trouvé');
                return;
            }

            // Construire les adresses complètes
            const address1 = `${principalAddress.address}, ${principalAddress.postalCode} ${principalAddress.city}`;
            const address2 = `${address.address}, ${address.postalCode} ${address.city}`;

            // Interroger l'AI
            const result = await AIHandler.query(
                `Adresse 1: ${address1}\nAdresse 2: ${address2}`,
                distancePrompt.prompt
            );

            // Extraire le nombre de la réponse
            const distanceMatch = result.match(/\d+([.,]\d+)?/);
            if (distanceMatch) {
                const distance = parseFloat(distanceMatch[0].replace(',', '.'));
                
                // Mettre à jour l'adresse avec la nouvelle distance
                setAccountData(prev => ({
                    ...prev,
                    addresses: prev.addresses.map(addr => {
                        if (addr.address === address.address && 
                            addr.postalCode === address.postalCode && 
                            addr.city === address.city) {
                            return { ...addr, distanceKm: distance };
                        }
                        return addr;
                    })
                }));
            }

        } catch (error) {
            console.error('Erreur lors du calcul de la distance:', error);
        }
    };

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            {notification.show && (
                <NotificationSystem
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification({ ...notification, show: false })}
                />
            )}
            <div className="modal-background" onClick={onClose}></div>
            <div className="modal-card modal-card-wide">
                <header className="modal-card-head">
                    <p className="modal-card-title">
                        {account ? 'Modifier le compte' : 'Nouveau compte'}
                    </p>
                    <button className="delete" aria-label="close" onClick={onClose}></button>
                </header>
                <section className="modal-card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="modal-subpanel">
                            <div className="modal-subpanel-header">
                                <span>Information générale</span>
                            </div>
                            <div className="table-container">
                                <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                    <tbody>
                                        <tr>
                                            <td style={{ width: '100px' }}>
                                                <label className="label has-text-weight-bold">Nom</label>
                                            </td>
                                            <td>
                                                <input
                                                    className="input"
                                                    type="text"
                                                    value={accountData.name}
                                                    onChange={(e) => setAccountData({ ...accountData, name: e.target.value })}
                                                    placeholder="Nom"
                                                    required
                                                />
                                            </td>
                                            <td style={{ width: '100px' }}>
                                                <label className="label has-text-weight-bold">Type</label>
                                            </td>
                                            <td>
                                                <div className="select is-fullwidth">
                                                    <select
                                                        value={accountData.typeId || ''}
                                                        onChange={(e) => setAccountData({ ...accountData, typeId: Number(e.target.value) })}
                                                        required
                                                    >
                                                        {accountTypes.map(type => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.data}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </td>
                                            <td style={{ width: '100px' }}>
                                                <label className="label has-text-weight-bold">N° TVA</label>
                                            </td>
                                            <td>
                                                <input
                                                    className="input"
                                                    type="text"
                                                    value={accountData.taxId || ''}
                                                    onChange={(e) => setAccountData({ ...accountData, taxId: e.target.value })}
                                                    placeholder="N° TVA intracommunautaire"
                                                />
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        <div className="modal-subpanel" style={{ marginTop: '1rem' }}>
                            <div className="modal-subpanel-header">
                                <span>Adresses</span>
                                <button type="button" className="button is-small" onClick={addAddress}>
                                    <span className="icon is-small icon-primary">
                                        <FontAwesomeIcon icon={faPlus} />
                                    </span>
                                </button>
                            </div>
                            <div className="table-container">
                                <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                    <thead className="modal-table-header">
                                        <tr>
                                            <th style={{ width: '100px' }}>Princ.</th>
                                            <th style={{ width: '100px' }}>Fact.</th>
                                            <th>Adresse</th>
                                            <th style={{ width: '120px' }}>Code postal</th>
                                            <th style={{ width: '250px' }}>Ville</th>
                                            <th style={{ width: '120px' }}>Distance (km)</th>
                                            <th style={{ width: '80px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(accountData?.addresses || []).map((addr, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <label className="radio">
                                                        <input
                                                            type="radio"
                                                            checked={addr.isPrimary}
                                                            onChange={() => updateAddress(index, true, 'isPrimary')}
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    <label className="radio">
                                                        <input
                                                            type="radio"
                                                            checked={addr.isBillingLocation}
                                                            onChange={() => updateAddress(index, true, 'isBillingLocation')}
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        value={addr.address}
                                                        onChange={(e) => updateAddress(index, e.target.value, 'address')}
                                                        placeholder="Adresse"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        value={addr.postalCode}
                                                        onChange={(e) => updateAddress(index, e.target.value, 'postalCode')}
                                                        placeholder="Code postal"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        value={addr.city}
                                                        onChange={(e) => updateAddress(index, e.target.value, 'city')}
                                                        placeholder="Ville"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="number"
                                                        min="0"
                                                        value={addr.distanceKm || 0}
                                                        onChange={(e) => updateAddress(index, Number(e.target.value), 'distanceKm')}
                                                        placeholder="Distance"
                                                    />
                                                </td>
                                                <td>
                                                    <div className="table-actions-container">
                                                        {!isDemo && accountTypes.find(type => 
                                                            type.id === accountData.typeId && 
                                                            type.data !== 'Principal'
                                                        ) && (
                                                            <button 
                                                                type="button" 
                                                                className="button is-small is-info table-action-button" 
                                                                onClick={() => calculateDistance(addr)}
                                                            >
                                                                <span className="icon icon-info">
                                                                    <FontAwesomeIcon icon={faCalculator} />
                                                                </span>
                                                            </button>
                                                        )}
                                                        {!addr.isPrimary && (
                                                            <button 
                                                                type="button" 
                                                                className="button is-small is-danger table-action-button" 
                                                                onClick={() => removeAddress(index)}
                                                            >
                                                                <span className="icon icon-danger">
                                                                    <FontAwesomeIcon icon={faTrashAlt} />
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="modal-subpanel" style={{ marginTop: '1rem' }}>
                            <div className="modal-subpanel-header">
                                <span>Contacts</span>
                                <button type="button" className="button is-small" onClick={addContact}>
                                    <span className="icon is-small icon-primary">
                                        <FontAwesomeIcon icon={faPlus} />
                                    </span>
                                </button>
                            </div>
                            <div className="table-container">
                                <table className="table is-fullwidth is-striped is-hoverable mb-0">
                                    <thead className="modal-table-header">
                                        <tr>
                                            <th style={{ width: '100px' }}>Princ.</th>
                                            <th style={{ width: '100px' }}>Fact.</th>
                                            <th>Prénom</th>
                                            <th>Nom</th>
                                            <th>Email</th>
                                            <th style={{ width: '150px' }}>Téléphone</th>
                                            <th style={{ width: '80px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(accountData?.contacts || []).map((contact, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <label className="radio">
                                                        <input
                                                            type="radio"
                                                            checked={contact.isPrimary}
                                                            onChange={() => updateContact(index, true, 'isPrimary')}
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    <label className="radio">
                                                        <input
                                                            type="radio"
                                                            checked={contact.isBillingContact}
                                                            onChange={() => updateContact(index, true, 'isBillingContact')}
                                                        />
                                                    </label>
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        value={contact.firstName}
                                                        onChange={(e) => updateContact(index, e.target.value, 'firstName')}
                                                        placeholder="Prénom"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="text"
                                                        value={contact.lastName}
                                                        onChange={(e) => updateContact(index, e.target.value, 'lastName')}
                                                        placeholder="Nom"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <div className="field">
                                                        <div className="control">
                                                            <input
                                                                className="input"
                                                                type="email"
                                                                value={contact.email}
                                                                onChange={(e) => updateContact(index, e.target.value, 'email')}
                                                                placeholder="Email"
                                                                required
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        className="input"
                                                        type="tel"
                                                        value={contact.phone}
                                                        onChange={(e) => updateContact(index, e.target.value, 'phone')}
                                                        placeholder="Téléphone"
                                                        required
                                                    />
                                                </td>
                                                <td>
                                                    <div className="table-actions-container">
                                                        {!contact.isPrimary && (
                                                            <button type="button" className="button is-small is-danger table-action-button" onClick={() => removeContact(index)}>
                                                                <span className="icon icon-danger">
                                                                <FontAwesomeIcon icon={faTrashAlt} />
                                                                </span>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </form>
                </section>
                <footer className="modal-card-foot">
                    <button className="button is-normal" onClick={onClose}>Annuler</button>
                    <button 
                        className="button is-primary is-normal" 
                        onClick={handleSubmit}
                        disabled={!isGeneralInfoValid} // Autoriser le clic si au moins les infos générales sont valides
                    >
                        {account ? 'Modifier' : 'Ajouter ce compte'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default AccountModal;
